import React, { useEffect, useState } from 'react'
import { hapticTick } from '../../../../../../_utils/haptics'
import { FONTS, fontStack, googleFontsHref } from '../../../email-composer/email/theme'
import { POST_TYPES, fieldsFor } from '../../data/postTypes'
import { DISPLAY_FONTS, TEXT_FONTS } from '../../render/families'
import { TEXT_SIZES, baseFamilyOf } from '../../render/render'
import { Field } from './Fields'
import ReviewPicker from './ReviewPicker'
import CodePicker from './CodePicker'
import { DISCOUNTS_VISIBLE } from '../../../../../../config/featureFlags'
import SlotPicker from './SlotPicker'
import { ChevronLeft, ChevronRight } from '../icons'

// The font list shows every name in its own face, so load them all once
let fontsLinked = false
const linkAllFonts = () => {
  if (fontsLinked || typeof document === 'undefined') return
  fontsLinked = true
  const href = googleFontsHref([...Object.keys(DISPLAY_FONTS), ...Object.keys(TEXT_FONTS)])
  if (!href) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

const SIZE_NAMES = ['Smallest', 'Smaller', 'As designed', 'Bigger', 'Biggest']

const Seg = ({ value, options, onChange, label }) => (
  <div className="sx-seg" role="radiogroup" aria-label={label}>
    {options.map(o => (
      <button key={o.id} type="button" role="radio" aria-checked={value === o.id} disabled={o.disabled} className={`sx-seg-btn${value === o.id ? ' is-on' : ''}`} onClick={() => { if (value !== o.id) { hapticTick(); onChange(o.id) } }}>
        {o.label}
      </button>
    ))}
  </div>
)

const FontList = ({ title, current, designed, brandFont, options, onPick, onBack, sample }) => {
  useEffect(() => { linkAllFonts() }, [])
  const keys = [designed, ...(brandFont && brandFont !== designed && options[brandFont] ? [brandFont] : []), ...Object.keys(options).filter(k => k !== designed && k !== brandFont)]
  return (
    <div className="sx-font-edit">
      <button type="button" className="sx-back" onClick={onBack}><ChevronLeft size={16} /><span>{title}</span></button>
      <div className="sx-font-list" role="radiogroup" aria-label={title}>
        {keys.map(k => (
          <button key={k} type="button" role="radio" aria-checked={current === k} className={`sx-font-tile${current === k ? ' is-on' : ''}`} onClick={() => { hapticTick(); onPick(k === designed ? undefined : k) }}>
            <span className="sx-font-sample" style={{ fontFamily: fontStack(k), fontWeight: sample(k) }}>{(FONTS[k] || {}).label || k}</span>
            <span className="sx-font-note">{k === designed ? 'As designed' : k === brandFont ? 'Your brand' : ''}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// The words, then how they look: fonts, size, the salon's name and the
// booking line
// Free slots and a single slot pick their day and times from the diary
const SLOT_JOBS = ['freeSlots', 'oneSlot']

const TextPanel = ({ post, theme, kit, activeSlot, fromReview, onContent, onContents, onTune, onPickReview, onTypeOwnReview }) => {
  const [editing, setEditing] = useState(null) // 'font' | 'textFont'
  const def = POST_TYPES[post.type]
  const base = baseFamilyOf(post, kit)
  const tune = post.tune || {}
  const sizeIndex = Math.max(0, TEXT_SIZES.indexOf(theme.size))
  const hasCta = def.fields.some(f => f.key === 'cta')

  if (editing === 'font') {
    return (
      <FontList
        title="Headline font"
        current={theme.family.display}
        designed={base.display}
        brandFont={kit.display}
        options={DISPLAY_FONTS}
        sample={(k) => (k === base.display ? base.displayWeight : DISPLAY_FONTS[k] ? DISPLAY_FONTS[k].weight : 400)}
        onPick={(k) => onTune({ font: k }, 'font')}
        onBack={() => setEditing(null)}
      />
    )
  }
  if (editing === 'textFont') {
    return (
      <FontList
        title="Text font"
        current={theme.family.body.font}
        designed={base.body.font}
        brandFont={kit.text}
        options={TEXT_FONTS}
        sample={(k) => (TEXT_FONTS[k] ? TEXT_FONTS[k].label : 600)}
        onPick={(k) => onTune({ textFont: k }, 'textFont')}
        onBack={() => setEditing(null)}
      />
    )
  }

  return (
    <div className="sx-text-panel">
      {post.type === 'review' && onPickReview && <ReviewPicker post={post} onPick={onPickReview} onTypeOwn={onTypeOwnReview} />}
      {post.type === 'offer' && DISCOUNTS_VISIBLE && onContents && <CodePicker post={post} onContents={onContents} />}
      <section className="sx-group" aria-label="Words">
        {fieldsFor(post).map((field, i, all) => {
          const slots = SLOT_JOBS.includes(post.type)
          // The day field becomes the diary picker for the day and times
          if (slots && (field.key === 'times' || field.key === 'time')) return null
          if (slots && field.key === 'date') {
            const times = all.find(f => f.key === 'times')
            return (
              <div key="slots" className={['date', 'times', 'time'].includes(activeSlot) ? 'sx-field-wrap sx-slot-wrap is-on' : 'sx-field-wrap sx-slot-wrap'}>
                <SlotPicker
                  post={post}
                  single={post.type === 'oneSlot'}
                  max={times ? times.max : 3}
                  onChange={patch => (onContents ? onContents(patch, 'slots') : Object.keys(patch).forEach(k => onContent(k, patch[k])))}
                  onMore={() => onContent('more', true)}
                />
              </div>
            )
          }
          return (
            <div key={field.key} className={activeSlot === field.key ? 'sx-field-wrap is-on' : 'sx-field-wrap'}>
              <Field field={field} value={post.content[field.key]} onChange={(v) => onContent(field.key, v)} locked={(field.locked || field.key === 'rating') && fromReview} post={post} />
            </div>
          )
        })}
      </section>

      <section className="sx-group" aria-label="How the words look">
        <h3 className="sx-group-title">How it looks</h3>
        <button type="button" className="sx-colour-row" onClick={() => { hapticTick(); setEditing('font') }}>
          <span className="sx-font-mini" style={{ fontFamily: fontStack(theme.family.display) }}>Aa</span>
          <span className="sx-colour-name">Headline font</span>
          <span className="sx-colour-hex">{(FONTS[theme.family.display] || {}).label}</span>
          <ChevronRight size={16} />
        </button>
        <button type="button" className="sx-colour-row" onClick={() => { hapticTick(); setEditing('textFont') }}>
          <span className="sx-font-mini is-small" style={{ fontFamily: fontStack(theme.family.body.font) }}>Aa</span>
          <span className="sx-colour-name">Text font</span>
          <span className="sx-colour-hex">{(FONTS[theme.family.body.font] || {}).label}</span>
          <ChevronRight size={16} />
        </button>

        <div className="sx-field">
          <span className="sx-label">Headline size</span>
          <div className="sx-stepper" role="group" aria-label="Headline size">
            <button type="button" className="sx-stepper-btn" disabled={sizeIndex === 0} onClick={() => { hapticTick(); onTune({ size: TEXT_SIZES[sizeIndex - 1] === 1 ? undefined : TEXT_SIZES[sizeIndex - 1] }, 'size') }} aria-label="Smaller">
              <span aria-hidden>A</span>
            </button>
            <span className="sx-stepper-value" aria-live="polite">
              <span className="sx-stepper-dots" aria-hidden>{TEXT_SIZES.map((s, i) => <i key={s} className={i === sizeIndex ? 'is-on' : ''} />)}</span>
              {SIZE_NAMES[sizeIndex]}
            </span>
            <button type="button" className="sx-stepper-btn is-big" disabled={sizeIndex === TEXT_SIZES.length - 1} onClick={() => { hapticTick(); onTune({ size: TEXT_SIZES[sizeIndex + 1] === 1 ? undefined : TEXT_SIZES[sizeIndex + 1] }, 'size') }} aria-label="Bigger">
              <span aria-hidden>A</span>
            </button>
          </div>
          <span className="sx-hint">Long words shrink to fit, whatever the size.</span>
        </div>

        <div className="sx-field">
          <span className="sx-label">Your name on the post</span>
          <Seg
            label="Your name on the post"
            value={theme.brandMode}
            onChange={(v) => onTune({ brand: v === 'name' ? undefined : v }, 'brand')}
            options={[{ id: 'name', label: 'Name' }, { id: 'logo', label: 'Logo', disabled: !kit.logo }, { id: 'none', label: 'Neither' }]}
          />
          {!kit.logo && <span className="sx-hint">Add your logo in Marketing, Config, and you can put it on posts.</span>}
        </div>

        {hasCta && (
          <label className="sx-toggle" htmlFor="sx-show-cta">
            <input id="sx-show-cta" type="checkbox" checked={!tune.hideCta} onChange={(e) => { const on = e.target.checked; hapticTick(); onTune({ hideCta: on ? undefined : true }, 'cta') }} />
            <span className="sx-toggle-track" aria-hidden><span /></span>
            <span className="sx-label">Show the booking line</span>
          </label>
        )}
        {(tune.font || tune.textFont || tune.size || tune.brand || tune.hideCta) && (
          <button type="button" className="sx-link sx-reset" onClick={() => { hapticTick(); onTune({ font: undefined, textFont: undefined, size: undefined, brand: undefined, hideCta: undefined }, 'text-reset') }}>
            Use the design’s own type again
          </button>
        )}
      </section>
    </div>
  )
}

export default TextPanel
